import { Component, OnInit, OnDestroy, ChangeDetectionStrategy } from '@angular/core';

import { ReactiveFormsModule, FormBuilder, FormGroup, FormArray, Validators } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatRadioModule } from '@angular/material/radio';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatChipsModule } from '@angular/material/chips';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { Subject, takeUntil } from 'rxjs';

import { KeycloakClient } from '../../../core/models/client.model';
import { ClientService } from '../../../core/services/client.service';

@Component({
  selector: 'app-edit-client',
  imports: [
    ReactiveFormsModule,
    RouterModule,
    MatCardModule,
    MatButtonModule,
    MatIconModule,
    MatInputModule,
    MatFormFieldModule,
    MatSelectModule,
    MatCheckboxModule,
    MatRadioModule,
    MatSlideToggleModule,
    MatChipsModule,
    MatTooltipModule,
    MatProgressSpinnerModule,
    MatProgressBarModule,
    MatSnackBarModule
],
  templateUrl: './edit-client.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./edit-client.component.scss']
})
export class EditClientComponent implements OnInit, OnDestroy {
  client: KeycloakClient | null = null;
  clientId: string | null = null;
  clientForm!: FormGroup;
  loading = true;
  saving = false;
  error: string | null = null;

  private destroy$ = new Subject<void>();
  private originalClient: KeycloakClient | null = null;

  // Ajouter ces propriétés
  basicInfoExpanded = true;
  clientTypeExpanded = true;
  urlsExpanded = true;
  flowsExpanded = true;
  advancedExpanded = false;

  clientTypes = [
    { value: 'external-app', label: 'Externe' },
    { value: 'internal-app', label: 'Interne' },
    { value: 'neutral-app', label: 'Neutre' }
  ];

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private clientService: ClientService,
    private fb: FormBuilder,
    private snackBar: MatSnackBar
  ) {
    this.initializeForm();
  }

  ngOnInit(): void {
    this.route.params
      .pipe(takeUntil(this.destroy$))
      .subscribe(params => {
        this.clientId = params['id'];
        if (this.clientId) {
          this.loadClientDetails();
        } else {
          this.error = 'ID client non fourni';
          this.loading = false;
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Initialise le formulaire avec les contrôles nécessaires
   */
  private initializeForm(): void {
    this.clientForm = this.fb.group({
      // Informations de base
      clientId: ['', Validators.required],
      name: [''],
      type: ['neutral-app'],
      description: [''],
      enabled: [true],

      // Type de client
      clientType: ['confidential', Validators.required],

      // URLs
      rootUrl: [''],
      baseUrl: [''],
      adminUrl: [''],
      redirectUris: this.fb.array([this.fb.control('')]),
      webOrigins: this.fb.array([this.fb.control('')]),

      // Flows OAuth
      standardFlowEnabled: [true],
      implicitFlowEnabled: [false],
      directAccessGrantsEnabled: [false],
      serviceAccountsEnabled: [false],

      // Configuration avancée
      protocol: ['openid-connect'],
      frontchannelLogout: [false],
      fullScopeAllowed: [true],
      consentRequired: [false],
      displayOnConsentScreen: [true]
    });

    // Surveillez les changements de type de client pour ajuster les flows
    this.clientForm.get('clientType')?.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(type => {
        this.adjustFlowsForClientType(type);
      });
  }

  /**
   * Charge les détails du client et met à jour le formulaire
   */
  loadClientDetails(): void {
    if (!this.clientId) return;

    this.loading = true;
    this.error = null;

    this.clientService.getClient(this.clientId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (client) => {
          this.client = client;
          this.originalClient = { ...client };
          this.populateForm(client);
          this.loading = false;
        },
        error: (error) => {
          console.error('Erreur lors du chargement du client:', error);
          this.error = `Erreur lors du chargement: ${error.message}`;
          this.loading = false;
        }
      });
  }

  /**
   * Remplit le formulaire avec les données du client
   */
  private populateForm(client: KeycloakClient): void {
    // Déterminer le type de client
    let clientType = 'confidential';
    if (client.publicClient) clientType = 'public';
    else if (client.bearerOnly) clientType = 'bearer';

    // Remplir les contrôles de base
    this.clientForm.patchValue({
      clientId: client.clientId,
      name: client.name || '',
      description: client.description || '',
      type: client.type || 'neutral-app',
      enabled: client.enabled,
      clientType: clientType,
      rootUrl: client.rootUrl || '',
      baseUrl: client.baseUrl || '',
      // adminUrl: client.adminUrl || '',
      standardFlowEnabled: client.standardFlowEnabled,
      implicitFlowEnabled: client.implicitFlowEnabled,
      directAccessGrantsEnabled: client.directAccessGrantsEnabled,
      serviceAccountsEnabled: client.serviceAccountsEnabled,
      protocol: client.protocol || 'openid-connect',
      // frontchannelLogout: client.frontchannelLogout || false,
      // fullScopeAllowed: client.fullScopeAllowed !== false,
      // consentRequired: client.consentRequired || false,
      // displayOnConsentScreen: client.displayOnConsentScreen !== false
    });

    // Remplir les arrays d'URLs
    this.populateUrlArrays(client);
  }

  /**
   * Remplit les arrays d'URLs
   */
  private populateUrlArrays(client: KeycloakClient): void {
    // URLs de redirection
    const redirectUris = client.redirectUris && client.redirectUris.length > 0
      ? client.redirectUris
      : [''];

    this.redirectUrisArray.clear();
    redirectUris.forEach(uri => {
      this.redirectUrisArray.push(this.fb.control(uri));
    });

    // Origines web
    const webOrigins = client.webOrigins && client.webOrigins.length > 0
      ? client.webOrigins
      : [''];

    this.webOriginsArray.clear();
    webOrigins.forEach(origin => {
      this.webOriginsArray.push(this.fb.control(origin));
    });
  }

  /**
   * Ajuste les flows disponibles selon le type de client
   */
  private adjustFlowsForClientType(clientType: string): void {
    const standardFlow = this.clientForm.get('standardFlowEnabled');
    const implicitFlow = this.clientForm.get('implicitFlowEnabled');
    const directAccess = this.clientForm.get('directAccessGrantsEnabled');
    const serviceAccounts = this.clientForm.get('serviceAccountsEnabled');

    switch (clientType) {
      case 'public':
        // Clients publics : généralement Standard Flow
        standardFlow?.enable();
        implicitFlow?.enable();
        directAccess?.enable();
        serviceAccounts?.disable();
        serviceAccounts?.setValue(false);
        break;

      case 'bearer':
        // Bearer only : aucun flow d'authentification
        standardFlow?.disable();
        implicitFlow?.disable();
        directAccess?.disable();
        serviceAccounts?.disable();
        [standardFlow, implicitFlow, directAccess, serviceAccounts].forEach(control => {
          control?.setValue(false);
        });
        break;

      case 'confidential':
      default:
        // Clients confidentiels : tous les flows disponibles
        [standardFlow, implicitFlow, directAccess, serviceAccounts].forEach(control => {
          control?.enable();
        });
        break;
    }
  }

  /**
   * Getters pour les FormArrays
   */
  get redirectUrisArray(): FormArray {
    return this.clientForm.get('redirectUris') as FormArray;
  }

  get webOriginsArray(): FormArray {
    return this.clientForm.get('webOrigins') as FormArray;
  }

  /**
   * Ajoute une nouvelle URL de redirection
   */
  addRedirectUri(): void {
    this.redirectUrisArray.push(this.fb.control(''));
  }

  /**
   * Supprime une URL de redirection
   */
  removeRedirectUri(index: number): void {
    if (this.redirectUrisArray.length > 1) {
      this.redirectUrisArray.removeAt(index);
    }
  }

  /**
   * Ajoute une nouvelle origine web
   */
  addWebOrigin(): void {
    this.webOriginsArray.push(this.fb.control(''));
  }

  /**
   * Supprime une origine web
   */
  removeWebOrigin(index: number): void {
    if (this.webOriginsArray.length > 1) {
      this.webOriginsArray.removeAt(index);
    }
  }

  /**
   * Sauvegarde les modifications du client
   */
  saveClient(): void {
    if (!this.clientForm.valid || !this.clientForm.dirty || !this.client) {
      return;
    }

    this.saving = true;
    const formValue = this.clientForm.value;

    // Préparer les données pour l'API
    const updatedClient = this.prepareClientData(formValue);

    this.clientService.updateClient(this.client.id, updatedClient)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (updated) => {
          this.client = updated;
          this.originalClient = { ...updated };
          this.clientForm.markAsPristine();
          this.saving = false;
          this.showSuccessMessage(`Client "${updated.name || updated.clientId}" mis à jour avec succès`);
        },
        error: (error) => {
          console.error('Erreur lors de la sauvegarde:', error);
          this.saving = false;
          this.showErrorMessage(`Erreur lors de la sauvegarde: ${error.message}`);
        }
      });
  }

  /**
   * Prépare les données du client pour l'API
   */
  private prepareClientData(formValue: any): Partial<KeycloakClient> {
    const clientType = formValue.clientType;

    // Filtrer les URLs vides
    const redirectUris = formValue.redirectUris.filter((uri: string) => uri.trim());
    const webOrigins = formValue.webOrigins.filter((origin: string) => origin.trim());

    return {
      clientId: formValue.clientId,
      name: formValue.name || undefined,
      description: formValue.description || undefined,
      type: formValue.type,
      enabled: formValue.enabled,

      // Type de client
      publicClient: clientType === 'public',
      bearerOnly: clientType === 'bearer',

      // URLs
      rootUrl: formValue.rootUrl || undefined,
      baseUrl: formValue.baseUrl || undefined,
      // adminUrl: formValue.adminUrl || undefined,
      redirectUris: redirectUris.length > 0 ? redirectUris : undefined,
      webOrigins: webOrigins.length > 0 ? webOrigins : undefined,

      // Flows
      standardFlowEnabled: formValue.standardFlowEnabled,
      implicitFlowEnabled: formValue.implicitFlowEnabled,
      directAccessGrantsEnabled: formValue.directAccessGrantsEnabled,
      serviceAccountsEnabled: formValue.serviceAccountsEnabled,

      // Configuration avancée
      protocol: formValue.protocol,
      // frontchannelLogout: formValue.frontchannelLogout,
      // fullScopeAllowed: formValue.fullScopeAllowed,
      // consentRequired: formValue.consentRequired,
      // displayOnConsentScreen: formValue.displayOnConsentScreen
    };
  }

  /**
   * Réinitialise le formulaire aux valeurs originales
   */
  resetForm(): void {
    if (this.originalClient) {
      this.populateForm(this.originalClient);
      this.clientForm.markAsPristine();
      this.showSuccessMessage('Formulaire réinitialisé');
    }
  }

  /**
   * Retourne à la page de détails du client
   */
  goBack(): void {
    if (this.clientForm.dirty) {
      const confirmed = confirm(
        'Des modifications non sauvegardées seront perdues. Voulez-vous vraiment quitter ?'
      );
      if (!confirmed) return;
    }

    this.router.navigate(['/dashboard/clients']);

    // if (this.clientId) {
    //   this.router.navigate(['/dashboard/clients', this.clientId, 'view']);
    // } else {
    //   this.router.navigate(['/dashboard/clients']);
    // }
  }

  /**
   * Génère les initiales pour l'avatar
   */
  getInitials(): string {
    if (!this.client) return '??';
    const displayName = this.client.name || this.client.clientId;
    return displayName.substring(0, 2).toUpperCase();
  }

  /**
   * Génère une couleur pour l'avatar
   */
  getAvatarColor(): string {
    if (!this.client) return '#45B56F';

    const colors = [
      '#45B56F', '#f093fb', '#45B56F', '#43e97b', '#fa709a',
      '#ff6b6b', '#4ecdc4', '#45b7d1', '#96ceb4', '#feca57'
    ];
    let hash = 0;
    for (let i = 0; i < this.client.clientId.length; i++) {
      hash = this.client.clientId.charCodeAt(i) + ((hash << 5) - hash);
    }
    return colors[Math.abs(hash) % colors.length];
  }

  /**
   * Affiche un message de succès
   */
  private showSuccessMessage(message: string): void {
    this.snackBar.open(message, 'Fermer', {
      duration: 4000,
      panelClass: ['success-snackbar'],
      horizontalPosition: 'end',
      verticalPosition: 'top'
    });
  }

  /**
   * Affiche un message d'erreur
   */
  private showErrorMessage(message: string): void {
    this.snackBar.open(message, 'Fermer', {
      duration: 6000,
      panelClass: ['error-snackbar'],
      horizontalPosition: 'end',
      verticalPosition: 'top'
    });
  }
}